// @ts-check
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Toast from '../components/ui/toast';

const ToastContext = createContext(/** @type {any} */ (null));

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 4000, onClick = undefined) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration, onClick }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Memoized so `useToast()` returns a stable reference — pages that put `toast` in a
  // useCallback/useEffect dependency array (the common `load` pattern across admin
  // pages) would otherwise re-run their effect every time ANY toast fires anywhere in
  // the app, since a new `value` object here re-renders every context consumer.
  const value = useMemo(
    () => ({
      showToast,
      success: (message, duration) => showToast(message, 'success', duration),
      error: (message, duration) => showToast(message, 'error', duration),
      warning: (message, duration) => showToast(message, 'warning', duration),
      info: (message, duration, onClick) => showToast(message, 'info', duration, onClick),
    }),
    [showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClick={toast.onClick}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};

export default ToastContext;
